/*
 * Copyright (C) 2023 Nick Robinson
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

package uk.co.nickthecoder.glok.theme

import uk.co.nickthecoder.glok.application.GlokSettings
import uk.co.nickthecoder.glok.application.Platform
import uk.co.nickthecoder.glok.property.ObservableValue
import uk.co.nickthecoder.glok.property.boilerplate.ObservableTheme
import uk.co.nickthecoder.glok.property.boilerplate.ThemeBinaryFunction
import uk.co.nickthecoder.glok.property.boilerplate.ThemeProperty
import uk.co.nickthecoder.glok.property.boilerplate.themeProperty
import uk.co.nickthecoder.glok.property.invalidationListener
import uk.co.nickthecoder.glok.scene.Node
import uk.co.nickthecoder.glok.scene.Scene

/**
 * Builds a [Theme]. A [Theme] can be thought of as the `rules` which change colors, size
 * and other attributes of [Node]s within a [Scene].
 *
 * A [ThemeBuilder] specifies these rules using a DSL (domain specific language).
 * The DSL is the equivalent to `CSS` (cascading style sheets) used by other GUI toolkits.
 *
 * ### Using a DSL instead of CSS has some advantages :
 *
 * * No need to learn new syntax. Everything is Kotlin code.
 * * Syntax highlighting, code completion etc. are handled by your IDE like any other Kotlin code.
 * * All the features of Kotlin are available, such as variables, if statements etc.
 * * Attributes can be _expressions_, rather than constants.
 *   e.g. Set a border size of `standardSize + 2`
 * * It is really easy to change property values, with a single assignment.
 *   All scenes are automatically updated.
 * * Most typos are detected at compile-time (or earlier - while typing in your IDE)
 * * No need to parse a text file at runtime.
 *
 * ### Disadvantages
 *
 * * They are executable code, not data, and therefore if you blindly incorporate a 3rd party's theme
 *   you are trusting them not to include malicious code. i.e. the same level of trust is required
 *   as you give to 3rd party Java/Kotlin libraries.
 * * ThemeBuilders must be in your classpath. You cannot easily incorporate a theme from a file.
 *
 * [Tantalum] is the default [ThemeBuilder]. It has many customisable properties, such as
 * a standard font size, a palette of colors etc.
 * Whenever any of these properties change, the [Theme] is rebuilt.
 * The [Theme] doesn't contain any `Properties`, only values.
 * So for example, if you change [Tantalum.accentColorProperty], a new [Theme] is created.
 * For performance reasons, the theme is rebuilt at the end of the current `frame`.
 * So if you change many properties at the same time, the theme is only rebuilt _once_.
 *
 * Rebuilding a theme is relatively expensive, so for example, do NOT constantly change
 * properties to achieve animation effects.
 *
 * FYI, [Tantalum] is often referred to as the _default theme_, despite it being a [ThemeBuilder]
 * and not a [Theme]. Using the phrase theme-builder is just too clunky, and most of the time,
 * the subtle distinction between the two types isn't important.
 */
abstract class ThemeBuilder {

    /**
     * The theme generated by [buildTheme].
     * [GlokSettings.defaultTheme] is typically bound to this property,
     * so that whenever any of this [ThemeBuilder]'s properties change, the entire application
     * will be re-themed.
     *
     * If you wish, you can use a different theme for individual [Scene]s.
     * In which case, you would bind [Scene.theme] to this property.
     * This feature is very rarely used.
     *
     * Initially, the theme is empty (it has no rules), and therefore you should set it within
     * the `init` section of each concrete implementation of [ThemeBuilder] :
     *
     *     init {
     *         theme = buildTheme()
     *     }
     */
    val themeProperty: ThemeProperty by themeProperty(Theme())
    var theme by themeProperty


    /**
     * This is where the magic happens. Your theme builder must implement this abstract method.
     *
     * Creates a [Theme] using Glok's Theme DSL (domain specific language).
     * See [Tantalum] for an example. There is also a simpler example in the `glok-demos` subproject
     * in the file `DemoCombineThemes.kt`.
     */
    abstract fun buildTheme(): Theme

    private var needsRebuilding = false

    private val needsRebuildingListener = invalidationListener {
        // A bodge to ensure that changes to each property doesn't rebuild the theme immediately,
        // and then the change to another property ALSO rebuilds the theme.
        // Without this, the theme is rebuilt many times when flipping from dark to light theme.
        needsRebuilding = true
        Platform.runLater {
            if (needsRebuilding) {
                theme = buildTheme()
            }
            needsRebuilding = false
        }
    }

    fun dependsOn(vararg properties: ObservableValue<*>) {
        for (prop in properties) {
            prop.addListener(needsRebuildingListener)
        }
    }

    /**
     * Combines two [ThemeBuilder]s. The result is an [ObservableTheme] whose value is a [Theme]
     * with rules from both of the [ThemeBuilder]s.
     *
     * `DemoCombineThemes` in the `glok-demos` subproject is a good example of how to combine themes.
     */
    infix fun combineWith(other: ThemeBuilder): ObservableTheme {
        return ThemeBinaryFunction(this.themeProperty, other.themeProperty) { myTheme, otherTheme ->
            myTheme combineWith otherTheme
        }
    }

}
